import {
  AsyncThunkPayloadCreator,
  createAsyncThunk as originalCreateAsyncThunk,
} from "@reduxjs/toolkit"
import type { AsyncThunkConfig } from "@reduxjs/toolkit"
import { v4 as uuidv4 } from "uuid"

const errors = new Map<string, unknown>()

function storeError(error: unknown): string {
  const key = uuidv4()

  if (errors.size >= 1000) {
    const firstKey = errors.keys().next().value
    if (firstKey) {
      errors.delete(firstKey)
    }
  }

  errors.set(key, error)
  return key
}

function getError(key: string): unknown {
  const error = errors.get(key)
  if (error !== undefined) errors.delete(key)
  return error
}

class FetchableThunkError extends Error {
  readonly name = "FetchableThunkError"
}

export function mtCreateAsyncThunk<
  Returned,
  ThunkArg = void,
  ThunkApiConfig extends AsyncThunkConfig = {},
>(
  typePrefix: string,
  payloadCreator: AsyncThunkPayloadCreator<Returned, ThunkArg, ThunkApiConfig>
) {
  return originalCreateAsyncThunk(
    typePrefix,
    async (arg: ThunkArg, thunkAPI) => {
      try {
        return await payloadCreator(arg, thunkAPI)
      } catch (error: unknown) {
        const key = storeError(error)
        throw new FetchableThunkError(key, { cause: error })
      }
    }
  )
}

export function tryFetchError(error: unknown): unknown {
  if (
    error &&
    typeof error === "object" &&
    "name" in error &&
    error.name === "FetchableThunkError" &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return getError(error.message)
  }

  return error
}

export const unwrapWithError = async <T>(dispatch: {
  unwrap: () => Promise<T>
}) => {
  try {
    return await dispatch.unwrap()
  } catch (e) {
    throw tryFetchError(e)
  }
}
